<template>
  <ChangePinModal
    :title="title"
    subTitle="Secure your transactions with a new PIN"
    @close="$emit('close')"
    :show="true"
    @go-back="goBack"
    :showBackButton="showBackButton"
    :bgChange="true"
  >
    <template v-slot:content>
      <section class="change-pin">
        <!-- Password Step -->
        <form @submit.prevent="validatePassword" v-if="step === 'Password'" class="change-pin__form">
          <div class="change-pin__step-header">
            <div class="change-pin__step-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <rect x="3" y="11" width="18" height="11" rx="2" stroke="currentColor" stroke-width="1.5"/>
                <path d="M7 11V7C7 4.23858 9.23858 2 12 2C14.7614 2 17 4.23858 17 7V11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
              </svg>
            </div>
            <h2 class="change-pin__step-title">Enter your password</h2>
            <p class="change-pin__step-desc">Please enter your account password to continue</p>
          </div>
          <div class="change-pin__form-group">
            <label class="change-pin__label">Password</label>
            <PasswordInput
              placeHolder="Enter password"
              label="Password"
              :password="password"
              :error="passwordError || {}"
              inputName="password"
              :disabled="loading"
              @set="setPassword"
              @validate="validateMethod"
            />
          </div>
          <CustomButton title="Continue" :loading="loading" />
        </form>

        <!-- Set PIN Step -->
        <form @submit.prevent="validatePin('Pin')" v-if="step === 'Pin'" class="change-pin__form">
          <div class="change-pin__step-header">
            <div class="change-pin__step-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="1.5"/>
                <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.5"/>
              </svg>
            </div>
            <h2 class="change-pin__step-title">Set new 4-digit PIN</h2>
            <p class="change-pin__step-desc">You'll use this PIN for signing in and confirming transactions</p>
          </div>
          <div class="change-pin__pin-wrapper">
            <PinInput
              @value-change="storePin($event, 'pin')"
              :fields="4"
              :clearPin="false"
            />
            <p class="change-pin__error" v-if="PinIsValidated && pin.length !== 4">
              Please enter a valid 4-digit PIN
            </p>
          </div>
          <CustomButton title="Continue" />
        </form>

        <!-- Confirm PIN Step -->
        <form @submit.prevent="validatePin('ConfirmPin')" v-if="step === 'ConfirmPin'" class="change-pin__form">
          <div class="change-pin__step-header">
            <div class="change-pin__step-icon change-pin__step-icon--success">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M20 6L9 17L4 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <h2 class="change-pin__step-title">Confirm your PIN</h2>
            <p class="change-pin__step-desc">Enter your new PIN again to confirm</p>
          </div>
          <div class="change-pin__pin-wrapper">
            <PinInput
              @value-change="storePin($event, 'confirmPin')"
              :fields="4"
              :clearPin="false"
            />
            <p class="change-pin__error" v-if="confirmPinIsValidated && confirmPin.length !== 4">
              Please enter a valid 4-digit PIN
            </p>
            <p class="change-pin__error" v-if="isNotSame">
              PINs do not match. Please try again.
            </p>
          </div>
          <CustomButton title="Set PIN" :loading="loading" />
        </form>
      </section>
    </template>
  </ChangePinModal>
</template>

<script>
import ChangePinModal from "@/components/general/sidemodal/SideModal";
import CustomButton from "@/components/onboarding/OnboardingBtn";
import PasswordInput from "@/components/onboarding/OnboardingPasswordInput";
import PinInput from "@/components/account/PinInput";
import { mapActions, mapGetters } from "vuex";
import useValidate from "@vuelidate/core";
import { required } from "@vuelidate/validators";
export default {
  name: "ChangePin",
  data() {
    return {
      v$: useValidate(),
      loading: false,
      title: "Change PIN",
      step: "Password",
      showBackButton: false,
      password: "",
      passwordError: {},
      pin: "",
      confirmPin: "",
      PinIsValidated: false,
      confirmPinIsValidated: false,
      isNotSame: false,
    };
  },
  components: {
    ChangePinModal,
    CustomButton,
    PasswordInput,
    PinInput,
  },
  validations() {
    return {
      password: { required },
    };
  },
  watch: {
    step(val) {
      if (val !== "Password") {
        this.showBackButton = true;
      }
    },
  },
  computed: {
    ...mapGetters("organisationModule", ["organisationDetails"]),
  },
  emits: ["close"],
  methods: {
    ...mapActions("organisationModule", ["login", "resetPin"]),
    ...mapActions(["showToast"]),
    setPassword(password) {
      this.password = password;
    },
    validateMethod(field) {
      switch (field) {
        case "Enter password":
          this.v$.$reset();
          this.v$.password.$touch();
          if (this.v$.$errors.length === 0) return (this.passwordError = {});
          this.passwordError = this.v$.$errors[0];
          break;
      }
    },
    goBack() {
      if (this.step === "Password") {
        this.$emit("close");
      } else if (this.step === "ConfirmPin") {
        this.step = "Pin";
      } else {
        this.step = "Password";
      }
    },
    storePin(value, pinType) {
      this[pinType] = value;
    },
    validatePin(type) {
      this.confirmPinIsValidated = false;
      this.PinIsValidated = false;
      switch (type) {
        case "Pin":
          this.PinIsValidated = true;
          if (this.pin.length === 4) {
            this.step = "ConfirmPin";
          }
          break;
        case "ConfirmPin":
          this.confirmPinIsValidated = true;
          if (this.confirmPin.length === 4) {
            if (this.confirmPin === this.pin) {
              this.changeTransactionPin();
              this.isNotSame = false;
            } else {
              this.isNotSame = true;
            }
          }
          break;
      }
    },
    async validatePassword() {
      this.loading = true;
      const email = this.organisationDetails.email;
      const payload = {
        password: this.password,
        email,
      };
      try {
        const response = await this.login(payload);
        if (response.status === 200) {
          this.step = "Pin";
        }
        this.loading = false;
      } catch (error) {
        this.loading = false;
        return error;
      }
    },
    async changeTransactionPin() {
      this.loading = true;
      try {
        const response = await this.resetPin({
          pin: this.pin,
          password: this.password,
        });
        this.loading = false;
        const { message } = response.data;
        this.showToast({
          description: message,
          display: true,
          type: "success",
        });
        this.$emit("close");
      } catch (error) {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.change-pin {
  padding: 0 24px 24px;

  &__form {
    max-width: 320px;
    margin: 0 auto;
  }

  &__step-header {
    text-align: center;
    margin-bottom: 32px;
  }

  &__step-icon {
    width: 56px;
    height: 56px;
    background: #F1F5F9;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 16px;
    color: #64748B;

    &--success {
      background: #DCFCE7;
      color: #16A34A;
    }
  }

  &__step-title {
    font-family: 'DM Sans', sans-serif;
    font-size: 18px;
    font-weight: 500;
    color: #0F172A;
    margin: 0 0 8px 0;
  }

  &__step-desc {
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: #64748B;
    margin: 0;
    line-height: 1.5;
  }

  &__form-group {
    margin-bottom: 24px;
  }

  &__label {
    display: block;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: #0F172A;
    margin-bottom: 8px;
  }

  &__pin-wrapper {
    margin-bottom: 32px;
    text-align: center;
  }

  &__error {
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    font-weight: 400;
    color: #EF4444;
    margin-top: 12px;
  }
}
</style>
